import React from 'react';
import * as R from 'ramda';
import { WithRouterProps } from 'react-router';

import ListingCard from 'ha/components/next/ListingCard';
import { formatDate } from 'ha/helpers/hadate';
import { ListingAttributes } from 'ha/components/next/ListingCard/types';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { Experiment, Variant } from 'ha/modules/Experiments';
import Experiments from 'ha/constants/experiments';

export const EVENT_NAME = 'Search listing clicked';

interface Props {
  listingId: number;
  pageInfo: { offset: number; limit: number };
  cityCanonical: string;
  countryCode: string;
  filtersAnalytics: {
    listingIdResults: number[];
    numberOfOrganicListings: number;
    numberOfPartnerListings: number;
    numberOfSearchResults: number;
  };
  listingsAnalytics: {
    listingIdResults: number[];
  };
  trackLocation?: string;
  track: (name: string, params: object) => void;
  isAdvertiserSearch: boolean;
  room: ListingAttributes;
  onMouseEnter?: (id: number) => void;
  onMouseLeave?: (id: number) => void;
  isLazy?: boolean;
  withGallery?: boolean;
  withFavorites?: boolean;
  isMapMarker?: boolean;
  isMobile: boolean;
}

const ListingPreviewCardTracked: React.FC<Props & WithRouterProps> = ({
  track,
  cityCanonical,
  countryCode,
  filtersAnalytics,
  pageInfo: { offset, limit },
  listingId,
  listingsAnalytics: { listingIdResults },
  isAdvertiserSearch,
  trackLocation,
  location: {
    query: { startDate, endDate },
  },
  isMobile,
  ...listingProps
}) => {
  const handleCardClick = React.useCallback(() => {
    const params = {
      type: 'link',
      cityCanonical,
      countryCode,
      ...R.omit(
        [
          'listingIdResults',
          'numberOfOrganicListings',
          'numberOfPartnerListings',
          'numberOfSearchResults',
        ],
        filtersAnalytics,
      ),
      position: offset + listingIdResults.indexOf(listingId) + 1,
      page: Math.floor(offset / limit) + 1,
      pageType: isAdvertiserSearch ? 'advertiser-search' : 'search',
      location: trackLocation,
    };

    track(EVENT_NAME, params);
  }, [
    track,
    cityCanonical,
    countryCode,
    filtersAnalytics,
    offset,
    limit,
    listingId,
    listingIdResults,
    isAdvertiserSearch,
    trackLocation,
  ]);

  const queryParams = React.useMemo(
    () => ({
      startDate: startDate ? formatDate('en', 'YYYY-MM-DD', startDate) : null,
      endDate: endDate ? formatDate('en', 'YYYY-MM-DD', endDate) : null,
    }),
    [startDate, endDate],
  );

  const { searchListingsSingleTab } = useFeatureFlags();
  const isFeatureFlagOn = searchListingsSingleTab === 'on';

  if (!isFeatureFlagOn || !isMobile) {
    return (
      <ListingCard
        {...listingProps}
        queryParams={queryParams}
        onCardClick={handleCardClick}
        linkTarget="_blank"
      />
    );
  }

  return (
    <Experiment name={Experiments.HA5815.name}>
      <Variant name="A">
        <ListingCard
          {...listingProps}
          queryParams={queryParams}
          onCardClick={handleCardClick}
          linkTarget="_blank"
        />
      </Variant>
      <Variant name="B">
        <ListingCard
          {...listingProps}
          queryParams={queryParams}
          onCardClick={handleCardClick}
          // Just to be _very_ explicit.
          linkTarget={undefined}
        />
      </Variant>
    </Experiment>
  );
};

export default ListingPreviewCardTracked;
